// src/pages/AdminQualityServices.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  FaUserSecret,
  FaClipboardCheck,
  FaChartLine,
  FaSearch,
  FaSyncAlt,
  FaCheck,
  FaTimes,
  FaCalendarCheck,
  FaFileAlt,
  FaDownload,
} from 'react-icons/fa';
import { adminQualityAction, qualityServiceLabel, qualityStatusLabel } from '../lib/qualityServices';

const statuses = ['all', 'submitted', 'in_review', 'scheduled', 'completed', 'cancelled'];

export default function AdminQualityServices() {
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [data, setData] = useState<any>({ requests: [], reports: [], metrics: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await adminQualityAction('list', { status: status === 'all' ? null : status, query });
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Impossible de charger les services qualité.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const metrics = data?.metrics || {};
  const requests = data?.requests || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((item: any) =>
      [item.business_name, item.contact_email, item.contact_name, item.region, item.visit_goal, item.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [requests, query]);

  const setRequestStatus = async (id: string, nextStatus: string) => {
    try {
      await adminQualityAction('update_status', { request_id: id, status: nextStatus });
      await load();
    } catch (err: any) {
      alert(err?.message || 'Action impossible.');
    }
  };

  const exportCsv = () => {
    const header = ['id', 'entreprise', 'email', 'type', 'region', 'statut', 'created_at'];
    const lines = filtered.map((r: any) => [
      r.id,
      r.business_name || '',
      r.contact_email || '',
      qualityServiceLabel(r.service_type),
      r.region || '',
      qualityStatusLabel(r.status),
      r.created_at || '',
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `forfeo-services-qualite-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6 text-white shadow-xl">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.18em]">
              <FaUserSecret />
              Services qualité
            </div>
            <h1 className="text-3xl font-black">Clients mystères & sondages</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-blue-100">
              Gérez les demandes, planifications, rapports et scores qualité des partenaires Forfeo.
            </p>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-black text-slate-950"
          >
            <FaDownload />
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          ['Demandes', metrics.requests_total ?? filtered.length, FaClipboardCheck],
          ['En attente', metrics.requests_pending ?? 0, FaSyncAlt],
          ['Complétées', metrics.requests_completed ?? 0, FaCheck],
          ['Score moyen', metrics.average_score ? `${metrics.average_score}/5` : '—', FaChartLine],
        ].map(([label, value, Icon]: any) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
              <Icon />
            </div>
            <p className="text-sm font-bold text-slate-500">{label}</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {statuses.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`rounded-full px-4 py-2 text-sm font-bold ${
                  status === s ? 'bg-slate-950 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {s === 'all' ? 'Tous' : qualityStatusLabel(s)}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <label className="relative block">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Rechercher..."
                className="w-full rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm outline-none focus:border-blue-500 lg:w-72"
              />
            </label>
            <button
              onClick={load}
              disabled={loading}
              className="rounded-full bg-slate-950 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
            >
              <FaSyncAlt className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {error && <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}

        <div className="mt-5 overflow-hidden rounded-2xl border border-slate-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">Entreprise</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Région</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      Aucune demande trouvée.
                    </td>
                  </tr>
                ) : (
                  filtered.map((request: any) => (
                    <tr key={request.id} className="align-top">
                      <td className="px-4 py-4">
                        <p className="font-black text-slate-950">{request.business_name || 'Entreprise à confirmer'}</p>
                        <p className="mt-1 text-xs text-slate-500">{new Date(request.created_at).toLocaleString('fr-CA')}</p>
                      </td>
                      <td className="px-4 py-4 font-bold text-slate-700">{qualityServiceLabel(request.service_type)}</td>
                      <td className="px-4 py-4">
                        <p className="font-bold text-slate-900">{request.contact_name || '—'}</p>
                        <p className="text-xs text-slate-500">{request.contact_email}</p>
                      </td>
                      <td className="px-4 py-4 text-slate-600">{request.region || '—'}</td>
                      <td className="px-4 py-4">
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                          {qualityStatusLabel(request.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => setRequestStatus(request.id, 'in_review')} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                            Analyse
                          </button>
                          <button onClick={() => setRequestStatus(request.id, 'scheduled')} className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">
                            <FaCalendarCheck className="inline" /> Planifier
                          </button>
                          <button onClick={() => setRequestStatus(request.id, 'completed')} className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">
                            <FaCheck className="inline" /> Compléter
                          </button>
                          <button onClick={() => setRequestStatus(request.id, 'cancelled')} className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-800">
                            <FaTimes className="inline" /> Annuler
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
